import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import Image, { type StaticImageData } from "next/image";
import clsx from "clsx";
import LottieView from "@/utils/lottie-helper";
import { track } from "@/utils/track";
import { Link } from "@/i18n/navigation";
import { formatCurrency } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import useGlobalModal from "@/components/modal/hooks/useGlobalModal";
import type { NotificationFetchwithmarkRes } from "@/types/api/vip/notification-fetchwithmark";
import { showRewardCardDrawer } from '@/features/reward/components/reward-card-drawer/drawerApi';
import badgeLevel1AnimJson from '@/features/user-vip/lottery/upgrade/vip1/up_elementary.json';
import badgeLevel6AnimJson from '@/features/user-vip/lottery/upgrade/vip6/up_intermediate.json';
import badgeLevel11AnimJson from '@/features/user-vip/lottery/upgrade/vip11/up_advanced.json';
import { vipLevelIconMap, type BenefitType } from "../../constant";
import { UPGRADE_THEMEDATA } from "../../themeData";
import iconCloseImg from '../../images/modal-close.png';
import addIcon from '../../images/icon-add.png';
import levelUpIcon from '../../images/upgrade/level-up-rewards.png';
import dailyIcon from '../../images/upgrade/daily-rewards.png';
import upgradeIcon from '../../images/upgrade/equity-upgrade.png';
import leftBg from '../../images/privileges/upgrade-left-bg.png';
import centerBg from '../../images/privileges/upgrade-center-bg.png';
import rightBg from '../../images/privileges/upgrade-right-bg.png';
import IconUpArrow from "../icons/upArrow";
import styles from './index.module.css';

interface RewardItemProps {
  title: string;
  subTitle: string;
  color: string;
  showAddIcon?: boolean;
  showUpgradeIcon?: boolean;
  bg: string;
  animClz?: string;
  animStyle?: CSSProperties;
  icon: StaticImageData;
}

function RewardItem(props: RewardItemProps) {
  return <>
    <div className="relative pb-[15px] flex flex-col items-center">
      <div className="w-[75px] h-[55px] flex items-center justify-center" style={{
        backgroundImage: `url(${props.bg})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat'
      }}>
        <Image src={props.icon} width={53} height={46}
          alt={props.subTitle}
          className={props.animClz} style={props.animStyle} />
      </div>
      <div className="flex items-center leading-none my-[7px] text-[12px] font-medium" style={{
        color: props.color
      }}>
        {props.title}
        {props.showUpgradeIcon && <div className="w-[7px] h-[10px] ml-[4px]"><IconUpArrow color={props.color} /></div>}
      </div>
      <div className="absolute bottom-0 left-[50%] translate-x-[-50%] whitespace-nowrap text-[10px] text-[#748094] font-medium">
        {props.subTitle}
      </div>
    </div>
    {props.showAddIcon && <Image src={addIcon} width={12} height={12} alt="add" className="translate-y-[-20px] mx-[10px] font-medium" />}
  </>;
}

interface VipUpgradeModalProps {
  data?: {
    notifications: NotificationFetchwithmarkRes['notifications'];
  };
}

export default function VipUpgradeModal({ data = {
  notifications: []
} }: VipUpgradeModalProps) {
  const { notifications = [] } = data;
  const t = useTranslations('vip');
  const { removeGlobalModal } = useGlobalModal();
  const [showLightAnim, setShowLightAnim] = useState(false);

  const notice = useMemo(() => {
    return notifications.filter((item) => {
      return item.type === 'member_upgrade';
    })?.[0];
  }, [notifications]);

  const summary = useMemo(() => {
    return JSON.parse(notice?.content || '{}') as {
      benefits: {
        trend: 'up' | 'down' | 'stable';
        status: 'new' | 'normal';
        type: BenefitType;
        amount: number;
        currency: string;
        times: number;
        lost_rate: number;
        extra: {
          amount: number;
          currency: string;
          guaranteed_amount: number;
          guaranteed_currency: string;
        };
      }[];
      membership: {
        level: number;
        min_point: number;
        amount: number;
        currency: string;
      };
    };
  }, [notice]);

  const { themeData, animData } = useMemo(() => {
    const level = summary?.membership?.level || 0;
    let themeData = UPGRADE_THEMEDATA.level11;
    let animData = badgeLevel11AnimJson;
    if (level < 6) {
      themeData = UPGRADE_THEMEDATA.level1;
      animData = badgeLevel1AnimJson;
    } else if (level < 11) {
      themeData = UPGRADE_THEMEDATA.level6;
      animData = badgeLevel6AnimJson;
    }
    return {
      themeData,
      animData
    };
  }, [summary?.membership?.level]);

  const handleClose = useCallback(() => {
    removeGlobalModal();
    // Claim Now、View Privileges、关闭-Close
    track('vip_popup_page_button_click', {
      button_name: 'Close'
    });
  }, [removeGlobalModal]);

  const showRewardDialog = () => {
    removeGlobalModal();
    showRewardCardDrawer();
    // Claim Now、View Privileges、关闭-Close
    track('vip_popup_page_button_click', {
      button_name: 'Claim Now'
    });
  };

  const getUpgradeData = useCallback((type: string) => {
    return summary?.benefits?.find(item => item.type === type)?.extra || {
      amount: 0,
      currency: 'INR',
      guaranteed_amount: 0,
      guaranteed_currency: 'INR'
    };
  }, [summary?.benefits]);

  const handlePrivileges = useCallback(() => {
    handleClose();
    // Claim Now、View Privileges、关闭-Close
    track('vip_popup_page_button_click', {
      button_name: 'View Privileges'
    });
  }, [handleClose]);

  // const getBadgeAnim = useCallback((level: number) => {
  //   if (level < 6) {
  //     return badgeLevel1AnimJson;
  //   } else if (level < 11) {
  //     return badgeLevel6AnimJson;
  //   }

  //   return badgeLevel11AnimJson;
  // }, []);

  const upgradeData = getUpgradeData('upgrade_reward');
  const dailyData = getUpgradeData('daily_reward');

  useEffect(() => {
    track('vip_popup_page_view', {
    });
    setTimeout(() => {
      setShowLightAnim(true);
    }, 300);
  }, []);

  return (<div className="absolute inset-0 flex flex-col flex-1 overflow-y-auto px-[12px] hide-scrollbar">
    <div className={clsx('absolute top-[50%] left-[50%] z-10 w-[320px] rounded-[10px]', styles.upgradeBgAnim)}
      style={{
        background: themeData.background
      }}>
      <div className="relative flex flex-col items-center text-[#fff] text-[15px] aspect-[320/155]"
        style={{
          backgroundImage: `url(${themeData.bg})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute top-[-55px] left-[50%] translate-x-[-50%]">
          <Image
            className={clsx(styles.upgradeBadgeAnim)}
            src={vipLevelIconMap[summary?.membership?.level - 1]}
            width={140} height={140} alt={t('newVip.claimBonus')}
          />
          {showLightAnim && <LottieView
            className={clsx('absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[200%] aspect-[1/1] pointer-events-none')}
            animationData={animData}
            autoplay={true}
            loop={false}
          />}
        </div>
        <div className="pt-[75px] font-semibold">{t('newVip.levelledUp', { level: '' })}</div>
        <div className="leading-none bg-clip-text [-webkit-text-fill-color:transparent] text-[30px] font-bold"
          style={{
            backgroundImage: themeData.vipColor
          }}
        >
          VIP{summary?.membership?.level || ''}
        </div>
      </div>
      <div className="px-[24px] pb-[20px]">
        <div className="flex items-center justify-center pt-[27px]">
          {upgradeData?.amount > 0 && <RewardItem
            icon={levelUpIcon}
            title={formatCurrency(upgradeData.amount || 0, upgradeData.currency || 'INR')}
            subTitle={t('benefits.levelUpBonus')}
            bg={leftBg.src}
            color={themeData.color}
            animClz={styles.upgradePrivilegeAnim}
            animStyle={{
              animationDelay: '1s'
            }}
            showAddIcon
          />}
          {dailyData?.guaranteed_amount > 0 && <RewardItem
            icon={dailyIcon}
            title={formatCurrency(dailyData.guaranteed_amount || 0, dailyData.guaranteed_currency || 'INR')}
            subTitle={t('benefits.dailyBonus')}
            bg={!upgradeData?.amount ? leftBg.src : centerBg.src}
            color={themeData.color}
            animClz={styles.upgradePrivilegeAnim}
            animStyle={{
              animationDelay: '1.1s'
            }}
            showAddIcon
          />}
          <RewardItem
            icon={upgradeIcon}
            title={t('newVip.perks')}
            subTitle={t('newVip.privilegeUpgrade')}
            bg={!upgradeData?.amount && !dailyData?.guaranteed_amount ? centerBg.src : rightBg.src}
            color={themeData.color}
            animClz={styles.upgradePrivilegeAnim}
            animStyle={{
              animationDelay: '1.2s'
            }}
            showUpgradeIcon
          />
        </div>
        <div className="flex flex-col items-center mt-[30px]">
          <Button className="w-full" variant="success" onClick={showRewardDialog}>
            {t('newVip.claimNow')}
          </Button>
          <Link href="/vip" onClick={handlePrivileges} className="flex items-center justify-center text-[12px] font-medium mt-[10px] text-[#97A1B6]">
            <span>{t('newVip.viewPrivileges')}</span>
          </Link>
        </div>
      </div>
      <div
        onClick={handleClose}
        className="absolute right-0 top-0 py-[15px] px-[20px] flex items-center justify-center"
      >
        <Image src={iconCloseImg} width={14} height={14} alt="close" />
      </div>
    </div>
  </div>);
}